import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Check, XCircle, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { apiInstance as api } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";
import { Avatar, Badge } from "./primitives";
import { Pagination, EmptyState } from "./DataTable";
import { Shimmer } from "./StatCard";

const STATUS_FILTERS = [
  { key: "all",      label: "All" },
  { key: "pending",  label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

function fmtMs(ms) {
  if (!ms || ms <= 0) return "0m";
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function memberName(user) {
  if (!user) return "Unknown";
  const n = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return n || user.email || "Unknown";
}

function StatusBadge({ status, t }) {
  const map = {
    pending:  { tone: "warning",  label: "Pending" },
    approved: { tone: "positive", label: "Approved" },
    rejected: { tone: "negative", label: "Rejected" },
  };
  const cfg = map[status] || { tone: "neutral", label: status };
  return <Badge tone={cfg.tone} dot>{cfg.label}</Badge>;
}

// ── Single request card ───────────────────────────────────────────────────────
function RequestCard({ req, t, onApprove, onReject, approving, rejecting }) {
  const [rejectNote, setRejectNote] = React.useState("");
  const [showReject, setShowReject] = React.useState(false);
  const name = memberName(req.userId);
  const isPending = req.status === "pending";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}
      style={{
        borderRadius: t.r.md, border: `1px solid ${t.c.border}`,
        background: t.c.surfaceMuted, overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: `${t.s[3]}px ${t.s[4]}px` }}>
        <Avatar user={req.userId} name={name} size={34} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: t.font.size.md, color: t.c.text }}>{name}</span>
            <StatusBadge status={req.status} t={t} />
          </div>
          <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 2 }}>
            {req.date} · {fmtDateTime(req.startTime)} → {fmtDateTime(req.endTime)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 8px", borderRadius: 99,
              background: t.status.warningSoft, color: t.status.warning,
              fontSize: t.font.size.xs, fontWeight: 600,
            }}>
              <Clock size={11} /> {fmtMs(req.durationMs)}
            </span>
            {req.reason && (
              <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                "{req.reason}"
              </span>
            )}
          </div>
          {req.status !== "pending" && req.reviewedBy && (
            <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 4 }}>
              {req.status === "approved" ? "Approved" : "Rejected"} by {memberName(req.reviewedBy)} · {fmtDateTime(req.reviewedAt)}
              {req.reviewNote && <span> · "{req.reviewNote}"</span>}
            </div>
          )}
        </div>

        {isPending && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => onApprove(req._id)}
              disabled={approving === req._id || rejecting === req._id}
              title="Approve"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                height: 30, padding: "0 10px", borderRadius: t.r.sm,
                border: `1px solid ${t.status.positive}`,
                background: "transparent", color: t.status.positive,
                fontSize: t.font.size.sm, fontWeight: 600, cursor: "pointer",
                fontFamily: t.font.family, opacity: approving === req._id ? 0.6 : 1,
              }}
            >
              <Check size={13} /> {approving === req._id ? "…" : "Approve"}
            </button>
            <button
              onClick={() => setShowReject((v) => !v)}
              disabled={approving === req._id || rejecting === req._id}
              title="Reject"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                height: 30, padding: "0 10px", borderRadius: t.r.sm,
                border: `1px solid ${t.status.negative}`,
                background: "transparent", color: t.status.negative,
                fontSize: t.font.size.sm, fontWeight: 600, cursor: "pointer",
                fontFamily: t.font.family, opacity: rejecting === req._id ? 0.6 : 1,
              }}
            >
              <XCircle size={13} /> Reject <ChevronDown size={11} style={{ transform: showReject ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
            </button>
          </div>
        )}
      </div>

      {/* Reject note input */}
      <AnimatePresence>
        {isPending && showReject && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ borderTop: `1px solid ${t.c.border}`, background: t.c.surface, overflow: "hidden" }}
          >
            <div style={{ padding: `${t.s[3]}px ${t.s[4]}px`, display: "flex", gap: t.s[2], alignItems: "flex-end" }}>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Reason for rejection (optional)"
                maxLength={500}
                rows={2}
                style={{
                  flex: 1, padding: "7px 10px", borderRadius: t.r.sm,
                  border: `1px solid ${t.c.border}`, background: t.c.surfaceMuted,
                  color: t.c.text, fontSize: t.font.size.sm, fontFamily: t.font.family,
                  resize: "none", outline: "none",
                }}
              />
              <button
                onClick={() => { onReject(req._id, rejectNote.trim() || null); setShowReject(false); }}
                disabled={rejecting === req._id}
                style={{
                  height: 34, padding: "0 14px", borderRadius: t.r.sm,
                  border: "none", background: t.status.negative, color: "#fff",
                  fontSize: t.font.size.sm, fontWeight: 600, cursor: "pointer",
                  fontFamily: t.font.family, flexShrink: 0,
                }}
              >
                {rejecting === req._id ? "…" : "Confirm"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Queue: filters + list + pagination ────────────────────────────────────────
// Shared by the modal below and the Inbox page, so approving a request behaves
// identically wherever it is reviewed.
export function IdleRequestsQueue({ onCountChange }) {
  const t = useDashTheme();
  const [statusFilter, setStatusFilter] = React.useState("pending");
  const [page, setPage] = React.useState(1);
  const [requests, setRequests] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [approving, setApproving] = React.useState(null);
  const [rejecting, setRejecting] = React.useState(null);
  const abortRef = React.useRef(null);

  React.useEffect(() => { setPage(1); }, [statusFilter]);

  // Fetch
  React.useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ page });
        if (statusFilter !== "all") params.append("status", statusFilter);
        const res = await api.get(`/api/v1/idle-requests?${params}`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (res.data.success) {
          const d = res.data.data;
          setRequests(d.requests || []);
          setTotal(d.total || 0);
          setTotalPages(d.totalPages || 1);
        } else {
          setError("Failed to load requests.");
        }
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") return;
        setError(err.response?.data?.error?.message || "Failed to load requests.");
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [statusFilter, page]);

  async function handleApprove(id) {
    setApproving(id);
    try {
      await api.put(`/api/v1/idle-requests/${id}/approve`);
      toast.success("Idle time approved — hours credited");
      setRequests((prev) => prev.map((r) => r._id === id ? { ...r, status: "approved" } : r));
      if (onCountChange) onCountChange(-1);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || "Failed to approve request");
    } finally {
      setApproving(null);
    }
  }

  async function handleReject(id, reviewNote) {
    setRejecting(id);
    try {
      await api.put(`/api/v1/idle-requests/${id}/reject`, { reviewNote });
      toast.success("Request rejected");
      setRequests((prev) => prev.map((r) => r._id === id ? { ...r, status: "rejected", reviewNote } : r));
      if (onCountChange) onCountChange(-1);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || "Failed to reject request");
    } finally {
      setRejecting(null);
    }
  }

  return (
    <>
      {/* Filter chips */}
      <div style={{ display: "flex", gap: 6, paddingBottom: t.s[3], borderBottom: `1px solid ${t.c.border}`, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((f) => {
          const active = statusFilter === f.key;
          return (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              style={{
                height: 30, padding: "0 12px", borderRadius: 99, cursor: "pointer",
                border: `1px solid ${active ? "transparent" : t.c.border}`,
                background: active ? t.c.primary : "transparent",
                color: active ? "#fff" : t.c.textSecondary,
                fontSize: t.font.size.sm, fontWeight: 500, fontFamily: t.font.family, transition: "all 140ms",
              }}
            >{f.label}</button>
          );
        })}
        {total > 0 && (
          <span style={{ marginLeft: "auto", fontSize: t.font.size.sm, color: t.c.textTertiary, alignSelf: "center" }}>
            {total} {total === 1 ? "request" : "requests"}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: `${t.s[4]}px 0`, display: "flex", flexDirection: "column", gap: t.s[2] }}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={{ padding: t.s[3], borderRadius: t.r.md, border: `1px solid ${t.c.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
              <Shimmer w="40%" h={12} /><Shimmer w="65%" h={10} /><Shimmer w="30%" h={10} />
            </div>
          ))
        ) : error ? (
          <EmptyState icon={<Clock size={22} />} title="Couldn't load requests" description={error} />
        ) : requests.length === 0 ? (
          <EmptyState icon={<Clock size={22} />} title="No requests" description={`No ${statusFilter === "all" ? "" : statusFilter + " "}idle time requests.`} />
        ) : (
          requests.map((req) => (
            <RequestCard
              key={req._id}
              req={req}
              t={t}
              onApprove={handleApprove}
              onReject={handleReject}
              approving={approving}
              rejecting={rejecting}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <Pagination page={page} pages={totalPages} total={total} pageLabel="requests" onChange={setPage} />
      )}
    </>
  );
}

// ── Modal wrapper (dashboard quick-review) ────────────────────────────────────
export function IdleRequestsPanel({ isOpen, onClose, pendingCount, onCountChange }) {
  const t = useDashTheme();

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: "absolute", inset: 0, background: "rgba(8,10,18,0.55)", backdropFilter: "blur(4px)" }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={t.motion.spring.snappy}
            style={{
              position: "relative", width: "100%", maxWidth: 680, maxHeight: "88vh",
              display: "flex", flexDirection: "column",
              background: t.c.surface, border: `1px solid ${t.c.border}`,
              borderRadius: t.r.lg, boxShadow: t.shadow.lg, overflow: "hidden",
              fontFamily: t.font.family,
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: 10, background: t.status.warningSoft, color: t.status.warning }}>
                  <Clock size={18} />
                </span>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>Idle time requests</span>
                    {pendingCount > 0 && (
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, borderRadius: 99, background: t.status.negative, color: "#fff", fontSize: t.font.size.xs, fontWeight: 700, padding: "0 5px" }}>
                        {pendingCount}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>Review and approve team idle time requests</div>
                </div>
              </div>
              <button onClick={onClose} aria-label="Close" style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: t.c.surfaceMuted, color: t.c.textSecondary, cursor: "pointer" }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1, padding: `${t.s[3]}px ${t.s[5]}px ${t.s[2]}px` }}>
              <IdleRequestsQueue onCountChange={onCountChange} />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
